/**
 * Create Family Member Screen
 *
 * Full-page form for creating a new family member.
 * Replaces the create modal with a dedicated screen.
 */

import { useCallback, useState, useMemo, useEffect } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMembers } from '@/hooks/use-family-members';
import { FamilyMemberForm } from '@/components/family/FamilyMemberForm';
import type { CreateFamilyMemberInput, UpdateFamilyMemberInput } from '@/types/family-member';

export default function CreateFamilyMemberScreen() {
  const router = useRouter();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const { createMember, getNextAvailableColor } = useFamilyMembers();

  const [defaultColor, setDefaultColor] = useState('#4CAF50');
  const [isSaving, setIsSaving] = useState(false);

  // Get the next available color on mount
  useEffect(() => {
    const loadDefaultColor = async () => {
      const nextColor = await getNextAvailableColor();
      setDefaultColor(nextColor);
    };
    loadDefaultColor();
  }, [getNextAvailableColor]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleCreate = useCallback(async (input: CreateFamilyMemberInput | UpdateFamilyMemberInput) => {
    try {
      setIsSaving(true);
      // Merge auto-assigned color into the create input
      const createInput: CreateFamilyMemberInput = {
        ...(input as CreateFamilyMemberInput),
        color: defaultColor,
      };
      await createMember(createInput);
      router.back();
    } catch (err) {
      console.error('Failed to create member:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [createMember, router, defaultColor]);

  const initialValues = useMemo(() => ({
    name: '',
    dateOfBirth: '',
    notes: '',
  }), []);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="close" size={24} color={palette.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
          New Member
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        {/* Title */}
        <Animated.View entering={FadeIn.duration(300)} style={styles.titleSection}>
          <Text style={[styles.title, { color: palette.text, fontFamily: getHeadingFont() }]}>
            Add a Family Member
          </Text>
          <Text style={[styles.subtitle, { color: palette.textMuted }]}>
            Create a new family member to organize your calendars.
          </Text>
        </Animated.View>

        {/* Form */}
        <FamilyMemberForm
          initialValues={initialValues}
          mode="create"
          isLoading={isSaving}
          onSubmit={handleCreate}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: Spacing.xs,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerSpacer: {
    width: 32,
  },
  keyboardView: {
    flex: 1,
  },
  titleSection: {
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.xxl,
    paddingBottom: Spacing.sm,
    gap: Spacing.sm,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
  },
  subtitle: {
    fontSize: 15,
  },
});
